import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EquipmentGearTable } from "@/components/equipment/EquipmentGearTable";
import { WorkerEquipmentModals } from "@/components/equipment/WorkerEquipmentModals";
import {
  assetStatusLabel,
  protocolDirectionLabel,
  protocolItemMeta,
  protocolStatusLabel,
  staffStatusLabel,
  staffTypeLabel,
} from "@/components/equipment/equipmentLabels";
import {
  canCancelProtocol,
  canHandoverAsset,
  canRespondToProtocol,
  canReturnAsset,
  canReturnStaffItem,
  cancelEquipmentProtocol,
  EquipmentApiError,
  isActiveStaffEquipment,
  listEquipmentAssets,
  listEquipmentProtocols,
  listStaffEquipment,
} from "@/lib/equipmentApi";
import { supabase } from "@/lib/supabase";
import type { EquipmentAsset, EquipmentProtocol, StaffEquipmentItem } from "@/types/equipment";
import { Plus } from "lucide-react";

interface WorkerAssignedEquipmentCardProps {
  orgId: string;
  workerId: string;
  canEdit: boolean;
}

export function WorkerAssignedEquipmentCard({
  orgId,
  workerId,
  canEdit,
}: WorkerAssignedEquipmentCardProps) {
  const [keys, setKeys] = useState<StaffEquipmentItem[]>([]);
  const [assets, setAssets] = useState<EquipmentAsset[]>([]);
  const [allAssets, setAllAssets] = useState<EquipmentAsset[]>([]);
  const [availableAssets, setAvailableAssets] = useState<EquipmentAsset[]>([]);
  const [protocols, setProtocols] = useState<EquipmentProtocol[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [keyOpen, setKeyOpen] = useState(false);
  const [toolOpen, setToolOpen] = useState(false);
  const [returnItem, setReturnItem] = useState<StaffEquipmentItem | EquipmentAsset | null>(null);
  const [returnKind, setReturnKind] = useState<"key_card" | "asset" | null>(null);
  const [respondProtocol, setRespondProtocol] = useState<EquipmentProtocol | null>(null);
  const [keyName, setKeyName] = useState("");
  const [assetId, setAssetId] = useState("");

  const load = useCallback(async () => {
    try {
      const [staffItems, allAssets, history] = await Promise.all([
        listStaffEquipment(orgId, workerId),
        listEquipmentAssets(orgId),
        listEquipmentProtocols({ orgId, workerId }),
      ]);
      setKeys(staffItems);
      setAllAssets(allAssets);
      setAssets(allAssets.filter((item) => item.current_holder_id === workerId));
      setAvailableAssets(allAssets.filter((item) => canHandoverAsset(item)));
      setProtocols(history);
    } catch (error) {
      console.error("[WorkerAssignedEquipmentCard]", error);
      toast.error(error instanceof EquipmentApiError ? error.message : "Nie udało się pobrać sprzętu.");
    }
  }, [orgId, workerId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? ""));
  }, []);

  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      setSaving(true);
      await action();
      toast.success(success);
      await load();
    } catch (error) {
      console.error("[WorkerAssignedEquipmentCard]", error);
      toast.error(error instanceof EquipmentApiError ? error.message : "Nie udało się zapisać protokołu.");
    } finally {
      setSaving(false);
    }
  };

  const activeKeys = keys.filter(isActiveStaffEquipment);
  const assignedTools = assets.filter((item) => item.status === "assigned" || item.status === "pending_return" || item.status === "pending_handover");

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Przypisany sprzęt</CardTitle>
            <CardDescription>Przedmioty i narzędzia. Historia zostaje po zdaniu.</CardDescription>
          </div>
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setKeyOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Przekaż przedmiot
              </Button>
              <Button variant="outline" onClick={() => setToolOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Przekaż narzędzie
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <EquipmentGearTable
          title="Aktywne"
          empty="Brak przypisanego sprzętu"
          rows={[
            ...activeKeys.map((item) => ({
              id: item.id,
              type: staffTypeLabel(item.type),
              name: item.name,
              status: staffStatusLabel(item.status),
              date: item.assigned_at,
              canReturn: canEdit && canReturnStaffItem(item),
              onReturn: () => {
                setReturnKind("key_card");
                setReturnItem(item);
              },
            })),
            ...assignedTools.map((item) => ({
              id: item.id,
              type: "Narzędzie",
              name: item.name,
              serialNumber: item.serial_number,
              condition: item.notes,
              status: assetStatusLabel(item.status),
              date: item.updated_at,
              canReturn: canEdit && canReturnAsset(item),
              onReturn: () => {
                setReturnKind("asset");
                setReturnItem(item);
              },
            })),
          ]}
        />

        <div>
          <h3 className="mb-2 text-sm font-medium">Historia protokołów</h3>
          {protocols.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Brak historii przekazania.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Czynność</TableHead>
                  <TableHead>Przedmiot</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {protocols.map((protocol) => {
                  const meta = protocolItemMeta(protocol, allAssets, keys);
                  return (
                  <TableRow key={protocol.id}>
                    <TableCell>{protocolDirectionLabel(protocol.direction)}</TableCell>
                    <TableCell>
                      <p className="font-medium">{meta.name}</p>
                      {meta.serialNumber && (
                        <p className="text-xs text-muted-foreground">Nr seryjny: {meta.serialNumber}</p>
                      )}
                      {protocol.status === "rejected" && protocol.rejection_reason && (
                        <p className="text-xs text-destructive">Powód odrzucenia: {protocol.rejection_reason}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={protocol.status === "pending" ? "outline" : "secondary"}>
                        {protocolStatusLabel(protocol.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(protocol.initiated_at), "dd.MM.yyyy HH:mm", { locale: pl })}
                    </TableCell>
                    <TableCell className="space-x-2">
                      {canRespondToProtocol(protocol, userId, canEdit) && (
                        <Button size="sm" variant="outline" onClick={() => setRespondProtocol(protocol)}>
                          Akceptuj / Odrzuć
                        </Button>
                      )}
                      {canCancelProtocol(protocol, userId, canEdit) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={saving}
                          onClick={() => void run(() => cancelEquipmentProtocol(protocol.id), "Protokół anulowany")}
                        >
                          Anuluj
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>

      <WorkerEquipmentModals
        orgId={orgId}
        workerId={workerId}
        saving={saving}
        run={run}
        keyOpen={keyOpen}
        setKeyOpen={setKeyOpen}
        keyName={keyName}
        setKeyName={setKeyName}
        toolOpen={toolOpen}
        setToolOpen={setToolOpen}
        assetId={assetId}
        setAssetId={setAssetId}
        availableAssets={availableAssets}
        assignedAssets={allAssets}
        assignedKeys={keys}
        returnItem={returnItem}
        returnKind={returnKind}
        setReturnItem={setReturnItem}
        setReturnKind={setReturnKind}
        respondProtocol={respondProtocol}
        setRespondProtocol={setRespondProtocol}
      />
    </Card>
  );
}
